// REST layer for the Retailer <-> Sales / Sales <-> Sales / Admin chat feature.
//
// Uses a DEDICATED axios instance (not the shared config/axios.ts one) because
// chat legitimately returns 403 (admin read-only / other user's chat) and 409
// (chat ended). The shared instance logs the user out on 403 and shows a
// "Customer Context Changed" popup on 409 — both wrong for chat. Here we only
// force logout on a genuine 401, and let 403/409 propagate to the caller.

import axios from "axios";
import { store } from "../store";
import { logout } from "../slices/authSlice";
import type {
  ChatAttachmentItem,
  ChatBroadcast,
  ChatChannel,
  ChatComplaintInvoice,
  ChatCustomer,
  ChatMessage,
  ChatPipelineCounts,
  ChatPipelineItem,
  ChatReaction,
  ChatSalesUser,
  RetailerSalesConversation,
  SalesSalesConversation,
  ChatConversation,
  AdminConversationsResponse,
} from "../../types/chat";

const chatAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5000/api",
  timeout: 600000,
});

chatAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

chatAxios.interceptors.response.use(
  (res) => res,
  (error) => {
    // Only a real auth failure should end the session; 403/409 are expected
    // chat states and must reach the caller.
    if (error?.response?.status === 401) {
      store.dispatch(logout());
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

const CHAT_BASE = "/chat";

const segFor = (channel: ChatChannel) =>
  channel === "retailer_sales" ? "retailer-sales" : "sales-sales";

// Unwrap the standard { success, message, data } envelope. Typed loosely because
// axios' bundled response type is AxiosResponse<unknown> here.
const unwrap = <T,>(res: any): T =>
  (res?.data?.data as T) ?? (undefined as unknown as T);

// ---- Retailer <-> Sales & Sales <-> Sales (shared shapes) ----

export async function listConversations(
  channel: ChatChannel
): Promise<Array<RetailerSalesConversation | SalesSalesConversation>> {
  const res = await chatAxios.get(`${CHAT_BASE}/${segFor(channel)}/conversations`);
  return unwrap(res) ?? [];
}

export interface GetMessagesResult {
  conversation: RetailerSalesConversation | SalesSalesConversation;
  messages: ChatMessage[];
}

export async function getMessages(
  channel: ChatChannel,
  conversationId: number,
  opts: { page?: number; limit?: number; before?: number } = {}
): Promise<GetMessagesResult> {
  const params: Record<string, number> = {};
  if (opts.page) params.page = opts.page;
  if (opts.limit) params.limit = opts.limit;
  if (opts.before) params.before = opts.before;
  const res = await chatAxios.get(
    `${CHAT_BASE}/${segFor(channel)}/conversations/${conversationId}/messages`,
    { params }
  );
  return unwrap(res);
}

// Send text and/or files. When starting a fresh thread, sales passes
// customerNumber (retailer_sales) or targetUserId (sales_sales).
export async function sendMessage(
  channel: ChatChannel,
  params: {
    conversationId?: number;
    customerNumber?: number;
    targetUserId?: number;
    content?: string;
    files?: File[];
    // A Giphy GIF (team chats only). Sent as a URL, not an upload — the server
    // re-validates the host and turns it into a normal image attachment.
    gifUrl?: string;
  }
): Promise<ChatMessage> {
  const fd = new FormData();
  if (params.content) fd.append("content", params.content);
  if (params.gifUrl) fd.append("gifUrl", params.gifUrl);
  if (params.conversationId != null)
    fd.append("conversationId", String(params.conversationId));
  if (params.customerNumber != null)
    fd.append("customerNumber", String(params.customerNumber));
  if (params.targetUserId != null)
    fd.append("targetUserId", String(params.targetUserId));
  (params.files || []).forEach((f) => fd.append("attachments", f));

  // Do NOT set Content-Type; the browser adds the multipart boundary.
  const res = await chatAxios.post(`${CHAT_BASE}/${segFor(channel)}/messages`, fd);
  return unwrap(res);
}

export async function editMessage(
  channel: ChatChannel,
  messageId: number,
  content: string
): Promise<ChatMessage> {
  const res = await chatAxios.patch(
    `${CHAT_BASE}/${segFor(channel)}/messages/${messageId}`,
    { content }
  );
  return unwrap(res);
}

export async function endChat(
  channel: ChatChannel,
  conversationId: number
): Promise<RetailerSalesConversation | SalesSalesConversation> {
  const res = await chatAxios.post(
    `${CHAT_BASE}/${segFor(channel)}/conversations/${conversationId}/end`
  );
  return unwrap(res);
}

// Soft-delete a message the caller sent. Regular users then see it as removed;
// the admin still sees the original content.
export async function deleteMessage(
  channel: ChatChannel,
  messageId: number
): Promise<void> {
  await chatAxios.delete(`${CHAT_BASE}/${segFor(channel)}/messages/${messageId}`);
}

// Delete ONE file out of a multi-attachment message; the message and its other
// files stay. Returns whether the message itself was retired (last file, no text).
export async function deleteAttachment(
  channel: ChatChannel,
  messageId: number,
  attachmentId: string
): Promise<{ messageDeleted: boolean }> {
  const res = await chatAxios.delete(
    `${CHAT_BASE}/${segFor(channel)}/messages/${messageId}/attachments/${attachmentId}`
  );
  return unwrap(res) ?? { messageDeleted: false };
}

// Sales reopens a chat they ended. The retailer gets the thread back.
export async function restartChat(
  conversationId: number
): Promise<RetailerSalesConversation> {
  const res = await chatAxios.post(
    `${CHAT_BASE}/retailer-sales/conversations/${conversationId}/restart`
  );
  return unwrap(res);
}

// Sales hands a customer chat to another sales person. After this the caller
// becomes view-only until it is transferred back to them.
export async function transferChat(
  conversationId: number,
  targetUserId: number,
  note?: string | null
): Promise<RetailerSalesConversation> {
  const res = await chatAxios.post(
    `${CHAT_BASE}/retailer-sales/conversations/${conversationId}/transfer`,
    { targetUserId, note: note ?? undefined }
  );
  return unwrap(res);
}

export async function markRead(
  channel: ChatChannel,
  conversationId: number
): Promise<void> {
  await chatAxios.post(`${CHAT_BASE}/${segFor(channel)}/messages/read`, {
    conversationId,
  });
}

// ---- Per-user chat state: archive + pin. Personal only — the other participant
// is unaffected. Both return the row's resulting flags.

export interface ChatStateResult {
  channel: ChatChannel;
  conversationId: number;
  isArchived: boolean;
  isPinned: boolean;
}

export async function setArchived(
  channel: ChatChannel,
  conversationId: number,
  archived: boolean
): Promise<ChatStateResult> {
  const res = await chatAxios.post(
    `${CHAT_BASE}/${segFor(channel)}/conversations/${conversationId}/archive`,
    { archived }
  );
  return unwrap(res);
}

export async function setPinnedChat(
  channel: ChatChannel,
  conversationId: number,
  pinned: boolean
): Promise<ChatStateResult> {
  const res = await chatAxios.post(
    `${CHAT_BASE}/${segFor(channel)}/conversations/${conversationId}/pin`,
    { pinned }
  );
  return unwrap(res);
}

// Search inside ONE conversation. Matches are newest-first and skip deleted
// messages; a retailer only ever searches their current session.
export async function searchMessages(
  channel: ChatChannel,
  conversationId: number,
  q: string,
  opts: { limit?: number; before?: number } = {}
): Promise<ChatMessage[]> {
  const params: Record<string, string | number> = { q };
  if (opts.limit) params.limit = opts.limit;
  if (opts.before) params.before = opts.before;
  const res = await chatAxios.get(
    `${CHAT_BASE}/${segFor(channel)}/conversations/${conversationId}/messages/search`,
    { params }
  );
  return unwrap<{ messages: ChatMessage[] }>(res)?.messages ?? [];
}

// React to a message. One reaction per person: sending the emoji you already used
// clears it, a different one swaps it. Returns the rebuilt summary for the message
// (the same payload also arrives over the socket for everyone else).
export async function reactToMessage(
  channel: ChatChannel,
  messageId: number,
  emoji: string
): Promise<{ messageId: number; reactions: ChatReaction[] }> {
  const res = await chatAxios.post(
    `${CHAT_BASE}/${segFor(channel)}/messages/${messageId}/reactions`,
    { emoji }
  );
  return unwrap(res);
}

// Everything shared in a conversation (images + files), newest first.
// Paginated by MESSAGE id — a single message can carry several files, so a page
// holds a variable number of items. Pass the returned `nextBefore` back to walk
// older; null means you've reached the start.
export async function listAttachments(
  channel: ChatChannel,
  conversationId: number,
  opts: { limit?: number; before?: number; q?: string } = {}
): Promise<{ items: ChatAttachmentItem[]; nextBefore: number | null }> {
  const params: Record<string, number | string> = {};
  if (opts.limit) params.limit = opts.limit;
  if (opts.before) params.before = opts.before;
  // Matches the sender's name or the file name.
  if (opts.q?.trim()) params.q = opts.q.trim();
  const res = await chatAxios.get(
    `${CHAT_BASE}/${segFor(channel)}/conversations/${conversationId}/attachments`,
    { params }
  );
  return unwrap(res) ?? { items: [], nextBefore: null };
}

// ---- Pinned messages — SHARED: pinning shows it to everyone in the chat.
// (Distinct from setPinnedChat above, which pins the whole chat just for you.)

export async function pinMessage(
  channel: ChatChannel,
  messageId: number
): Promise<ChatMessage> {
  const res = await chatAxios.post(
    `${CHAT_BASE}/${segFor(channel)}/messages/${messageId}/pin`
  );
  return unwrap(res);
}

export async function unpinMessage(
  channel: ChatChannel,
  messageId: number
): Promise<ChatMessage> {
  const res = await chatAxios.delete(
    `${CHAT_BASE}/${segFor(channel)}/messages/${messageId}/pin`
  );
  return unwrap(res);
}

export async function listPinnedMessages(
  channel: ChatChannel,
  conversationId: number
): Promise<ChatMessage[]> {
  const res = await chatAxios.get(
    `${CHAT_BASE}/${segFor(channel)}/conversations/${conversationId}/pinned`
  );
  return unwrap(res) ?? [];
}

// ---- Retailer guided issue flow (predefined questions) ----

export type ChatIssueType =
  | "delivery_misconduct"
  | "item_not_received"
  | "wrong_item"
  | "exp_item"
  | "damaged_item"
  | "other_order_issue"
  | "issue_not_listed";

export interface ChatIssueItem {
  itemNumber: string;
  description?: string | null;
  quantity?: number | null;
  returnStatus?: string | null; // 'R' returnable | 'N' no return | 'S' legacy
  manual?: boolean;
}

// Can the retailer raise an issue on this order? True for any of their own orders —
// there is no time window anymore. Drives the button on the order details page.
export async function getOrderIssueEligibility(orderNumber: number): Promise<{
  eligible: boolean;
  reason: string | null;
}> {
  const res = await chatAxios.get(`${CHAT_BASE}/orders/${orderNumber}/eligibility`);
  return unwrap(res);
}

// 'delivery_misconduct' only notifies the sales person (no chat message) — the
// response then carries `notified: true` and a null conversationId.
export async function submitChatIssue(params: {
  issueType: ChatIssueType;
  orderNumber?: number | null;
  items?: ChatIssueItem[];
  note?: string;
  files?: File[];
}): Promise<{ notified: boolean; conversationId: number | null; message: ChatMessage | null }> {
  const fd = new FormData();
  fd.append("issueType", params.issueType);
  if (params.orderNumber) fd.append("orderNumber", String(params.orderNumber));
  if (params.items?.length) fd.append("items", JSON.stringify(params.items));
  if (params.note) fd.append("note", params.note);
  (params.files || []).forEach((f) => fd.append("attachments", f));

  const res = await chatAxios.post(`${CHAT_BASE}/retailer-sales/issue`, fd);
  return unwrap(res);
}

// ---- Order-via-chat ----
// The retailer places an order as a free-form image/description; the rep advances it.

export async function submitOrderRequest(params: {
  description?: string;
  files?: File[];
}): Promise<{ conversationId: number; message: ChatMessage }> {
  const fd = new FormData();
  if (params.description) fd.append("description", params.description);
  (params.files || []).forEach((f) => fd.append("attachments", f));
  const res = await chatAxios.post(`${CHAT_BASE}/orders`, fd);
  return unwrap(res);
}

export async function startOrder(messageId: number): Promise<ChatMessage> {
  const res = await chatAxios.post(`${CHAT_BASE}/orders/${messageId}/start`);
  return unwrap(res);
}

export async function placeOrder(messageId: number, orderNumber: string): Promise<ChatMessage> {
  const res = await chatAxios.post(`${CHAT_BASE}/orders/${messageId}/place`, { orderNumber });
  return unwrap(res);
}

export async function dropOrder(messageId: number, reason: string): Promise<ChatMessage> {
  const res = await chatAxios.post(`${CHAT_BASE}/orders/${messageId}/drop`, { reason });
  return unwrap(res);
}

// ---- Sales pipeline ----

export async function getPipelineCounts(): Promise<ChatPipelineCounts> {
  const res = await chatAxios.get(`${CHAT_BASE}/pipeline/counts`);
  return (
    unwrap(res) ?? {
      orders: { pending: 0, started: 0, placed: 0 },
      issues: { pending_review: 0, in_progress: 0, resolving: 0, resolved: 0, rejected: 0, stopped: 0 },
    }
  );
}

// `category` is `order:<status>` or `issue:<status>`.
export async function listPipelineItems(category: string): Promise<ChatPipelineItem[]> {
  const res = await chatAxios.get(`${CHAT_BASE}/pipeline/items`, { params: { category } });
  return unwrap(res) ?? [];
}

// ---- Sales groups (sales_sales) — ADMIN only ----
// Sales users chat in groups but never create or manage them.

export async function createGroup(
  name: string,
  memberIds: number[]
): Promise<SalesSalesConversation> {
  const res = await chatAxios.post(`${CHAT_BASE}/sales-sales/groups`, { name, memberIds });
  return unwrap(res);
}

export async function addGroupMembers(
  conversationId: number,
  memberIds: number[]
): Promise<void> {
  await chatAxios.post(`${CHAT_BASE}/sales-sales/groups/${conversationId}/members`, {
    memberIds,
  });
}

export async function removeGroupMember(
  conversationId: number,
  userId: number
): Promise<void> {
  await chatAxios.delete(`${CHAT_BASE}/sales-sales/groups/${conversationId}/members/${userId}`);
}

// ---- Directories: valid targets for a NEW chat (sales only) ----

// Active sales users other than the caller. Use `id` as `targetUserId`.
export async function listSalesUsers(): Promise<ChatSalesUser[]> {
  const res = await chatAxios.get(`${CHAT_BASE}/sales-users`);
  return unwrap(res) ?? [];
}

// Customers assigned to the caller's sales rep(s). Use `C_Number` as
// `customerNumber`. Scoped server-side to match the send endpoint's guard.
export async function listChatCustomers(): Promise<ChatCustomer[]> {
  const res = await chatAxios.get(`${CHAT_BASE}/customers`);
  return unwrap(res) ?? [];
}

// ---- Complaint workflow (retailer_sales, sales side) ----
// The complaint id IS the issue message id. Every call returns the updated message,
// so the caller can drop it straight back into the thread.

// At every stage `note` is the exact message the customer receives — the sales
// person writes it, nothing static is sent for them.
export async function reviewComplaint(
  messageId: number,
  body: { isValid: boolean; note: string }
): Promise<ChatMessage> {
  const res = await chatAxios.post(`${CHAT_BASE}/complaints/${messageId}/review`, body);
  return unwrap(res);
}

// Stage 2 — the rep picks a resolution. Multipart: a return/replacement carries an RMA
// photo. Refund carries invoice + amount.
export async function chooseResolution(
  messageId: number,
  body: {
    resolutionType: "refund" | "replacement" | "return" | "other_product" | "other";
    note: string;
    invoiceNumber?: string;
    amount?: number;
    rmaFile?: File | null;
    // Item + quantity list for return/replacement/other-product.
    items?: { itemNumber: string; quantity: number }[];
  }
): Promise<ChatMessage> {
  const fd = new FormData();
  fd.append("resolutionType", body.resolutionType);
  fd.append("note", body.note);
  if (body.invoiceNumber) fd.append("invoiceNumber", body.invoiceNumber);
  if (body.amount != null) fd.append("amount", String(body.amount));
  if (body.rmaFile) fd.append("attachments", body.rmaFile);
  if (body.items?.length) fd.append("items", JSON.stringify(body.items));
  const res = await chatAxios.post(`${CHAT_BASE}/complaints/${messageId}/resolution`, fd);
  return unwrap(res);
}

// Stage 3 (return/replacement) — confirm which items (and how many) were received at
// the warehouse; resolves the complaint. Received quantities are validated server-side
// against what the retailer complained about.
export async function confirmReceipt(
  messageId: number,
  note: string,
  items: { itemNumber: string; quantity: number }[] = []
): Promise<ChatMessage> {
  const res = await chatAxios.post(`${CHAT_BASE}/complaints/${messageId}/confirm-receipt`, {
    note,
    items,
  });
  return unwrap(res);
}

// Sales asks the retailer to reply with a description / photo, saying what for (`note`).
// The retailer's chat renders a one-time reply box on the posted message.
export async function requestRetailerInfo(
  conversationId: number,
  note?: string
): Promise<ChatMessage> {
  const res = await chatAxios.post(
    `${CHAT_BASE}/retailer-sales/conversations/${conversationId}/request-info`,
    { note }
  );
  return unwrap(res);
}

// Retailer answers an info-request once (description + optional photos).
export async function replyToInfoRequest(
  messageId: number,
  description: string,
  files: File[]
): Promise<ChatMessage> {
  const fd = new FormData();
  if (description) fd.append("description", description);
  files.forEach((f) => fd.append("attachments", f));
  const res = await chatAxios.post(
    `${CHAT_BASE}/retailer-sales/info-requests/${messageId}/reply`,
    fd
  );
  return unwrap(res);
}

// Sales corrects a mis-chosen issue type while the complaint is still open.
export async function changeComplaintIssue(
  messageId: number,
  issueType: string
): Promise<ChatMessage> {
  const res = await chatAxios.post(`${CHAT_BASE}/complaints/${messageId}/issue-type`, { issueType });
  return unwrap(res);
}

// Open complaints/orders of one conversation, for the in-thread drawer.
export async function listConversationItems(
  conversationId: number,
  kind: "complaint" | "order"
): Promise<ChatMessage[]> {
  const res = await chatAxios.get(
    `${CHAT_BASE}/retailer-sales/conversations/${conversationId}/items`,
    { params: { kind } }
  );
  return unwrap(res) ?? [];
}

// Invoices of the complaint's own customer — the server derives the customer from
// the complaint, so there is nothing to scope from here.
export async function searchComplaintInvoices(
  messageId: number,
  search: string
): Promise<ChatComplaintInvoice[]> {
  const res = await chatAxios.get(`${CHAT_BASE}/complaints/${messageId}/invoices`, {
    params: { search },
  });
  return unwrap(res) ?? [];
}

// ---- Admin direct chat (admin <-> a sales user or retailer) ----
// A separate channel; the server already returns conversations/messages in the
// shared shape, so callers pass them straight through.

export interface AdminDirectoryResult {
  sales: { id: number; name: string }[];
  retailers: { id: number; name: string }[];
}

export async function listAdminDirectConversations(): Promise<ChatConversation[]> {
  const res = await chatAxios.get(`${CHAT_BASE}/admin-direct/conversations`);
  const rows = (unwrap<any[]>(res) ?? []) as any[];
  // Carry `raw` so the shared ChatConversation type is satisfied.
  return rows.map((c) => ({ ...c, raw: c }));
}

export async function getAdminDirectMessages(
  conversationId: number,
  opts: { limit?: number; before?: number } = {}
): Promise<GetMessagesResult> {
  const params: Record<string, number> = {};
  if (opts.limit) params.limit = opts.limit;
  if (opts.before) params.before = opts.before;
  const res = await chatAxios.get(
    `${CHAT_BASE}/admin-direct/conversations/${conversationId}/messages`,
    { params }
  );
  return unwrap(res) ?? ({ conversation: null as any, messages: [] } as any);
}

export async function sendAdminDirectMessage(params: {
  conversationId?: number;
  peerRole?: "sales" | "retailer";
  peerId?: number;
  content?: string;
  files?: File[];
}): Promise<ChatMessage> {
  const fd = new FormData();
  if (params.conversationId != null) fd.append("conversationId", String(params.conversationId));
  if (params.peerRole) fd.append("peerRole", params.peerRole);
  if (params.peerId != null) fd.append("peerId", String(params.peerId));
  if (params.content) fd.append("content", params.content);
  (params.files || []).forEach((f) => fd.append("attachments", f));
  const res = await chatAxios.post(`${CHAT_BASE}/admin-direct/messages`, fd);
  return unwrap(res);
}

// Admin opens (or reuses) a thread with a peer without sending yet.
export async function openAdminDirect(
  peerRole: "sales" | "retailer",
  peerId: number
): Promise<ChatConversation> {
  const res = await chatAxios.post(`${CHAT_BASE}/admin-direct/open`, { peerRole, peerId });
  const c = unwrap<any>(res);
  return { ...c, raw: c } as ChatConversation;
}

export async function markAdminDirectRead(conversationId: number): Promise<void> {
  await chatAxios.post(`${CHAT_BASE}/admin-direct/messages/read`, { conversationId });
}

// Distributor ends / restarts their direct chat with a retailer (or sales user).
export async function endAdminDirect(conversationId: number): Promise<ChatConversation> {
  const res = await chatAxios.post(
    `${CHAT_BASE}/admin-direct/conversations/${conversationId}/end`
  );
  const c = unwrap<any>(res);
  return { ...c, raw: c } as ChatConversation;
}
export async function restartAdminDirect(conversationId: number): Promise<ChatConversation> {
  const res = await chatAxios.post(
    `${CHAT_BASE}/admin-direct/conversations/${conversationId}/restart`
  );
  const c = unwrap<any>(res);
  return { ...c, raw: c } as ChatConversation;
}

export async function adminDirectory(search: string): Promise<AdminDirectoryResult> {
  const res = await chatAxios.get(`${CHAT_BASE}/admin-direct/directory`, { params: { search } });
  return unwrap(res) ?? { sales: [], retailers: [] };
}

// ---- Admin (distributor) — read-only ----

export async function listAdminConversations(
  opts: { channel?: ChatChannel; page?: number; limit?: number } = {}
): Promise<AdminConversationsResponse> {
  const params: Record<string, string | number> = {};
  if (opts.channel) params.channel = opts.channel;
  if (opts.page) params.page = opts.page;
  if (opts.limit) params.limit = opts.limit;
  const res = await chatAxios.get(`${CHAT_BASE}/admin/conversations`, { params });
  return unwrap(res) ?? { retailerSales: [], salesSales: [] };
}

export async function getAdminMessages(
  channel: ChatChannel,
  conversationId: number,
  opts: { page?: number; limit?: number } = {}
): Promise<ChatMessage[]> {
  const params: Record<string, number> = {};
  if (opts.page) params.page = opts.page;
  if (opts.limit) params.limit = opts.limit;
  const res = await chatAxios.get(
    `${CHAT_BASE}/admin/conversations/${channel}/${conversationId}/messages`,
    { params }
  );
  return unwrap(res) ?? [];
}

// ---- Distributor promo feed ("<D_Name> Bot") — read-only ----
// Its own two endpoints rather than the generic /:channel family, since archive,
// pin, reactions and search don't apply to a broadcast feed.

export async function listBroadcasts(
  opts: { limit?: number; before?: number } = {}
): Promise<{ conversation: ChatConversation; messages: ChatBroadcast[] }> {
  const params: Record<string, number> = {};
  if (opts.limit) params.limit = opts.limit;
  if (opts.before) params.before = opts.before;
  const res = await chatAxios.get(`${CHAT_BASE}/broadcasts`, { params });
  return unwrap(res) ?? { conversation: null as any, messages: [] };
}

export async function markBroadcastRead(): Promise<void> {
  await chatAxios.post(`${CHAT_BASE}/broadcasts/read`);
}

// ---- Normalizers: turn channel-specific conversations into the shared shape ----

export function toChatConversation(
  channel: ChatChannel,
  raw: RetailerSalesConversation | SalesSalesConversation,
  // The viewer's role decides who the "other side" is: a retailer is the customer,
  // so their thread must be titled with the sales rep, not with themselves.
  role?: string | null
): ChatConversation {
  // The promo feed rides along inside the retailer_sales list (it has no list of its
  // own), and the server already ships it in the final shape — so detect it from the
  // row's own channel rather than the requested one, and pass it straight through.
  if (channel === "distributor_broadcast" || (raw as any)?.channel === "distributor_broadcast") {
    return raw as unknown as ChatConversation;
  }
  if (channel === "retailer_sales") {
    const c = raw as RetailerSalesConversation;
    const title =
      role === "retailer"
        ? c.salesRepName || "My sales rep"
        : c.customerName
        ? `${c.customerName} (#${c.customerNumber})`
        : `Customer #${c.customerNumber}`;
    return {
      id: c.id,
      channel,
      title,
      lastMessageAt: c.lastMessageAt,
      lastMessagePreview: c.lastMessagePreview,
      unreadCount: c.unreadCount ?? 0,
      isEnded: c.isEnded,
      customerNumber: c.customerNumber,
      assignedToWebUserId: c.assignedToWebUserId ?? null,
      transferredBy: c.transferredBy ?? null,
      transferredByName: c.transferredByName ?? null,
      assignedToName: c.assignedToName ?? null,
      transferredAt: c.transferredAt ?? null,
      lastTransferNote: c.lastTransferNote ?? null,
      transferHistory: c.transferHistory ?? null,
      isArchived: !!c.isArchived,
      isPinned: !!c.isPinned,
      raw: c,
    };
  }
  const c = raw as SalesSalesConversation;
  const isGroup = c.type === "group";
  // The admin has no counterpart on a peer thread — name it by its members.
  const memberNames = (c.members || []).map((m) => m.name).filter(Boolean) as string[];
  const directTitle =
    c.counterpartName ||
    (memberNames.length ? memberNames.join(" ↔ ") : `Sales #${c.counterpartId ?? ""}`);
  return {
    id: c.id,
    channel,
    title: isGroup ? c.name || "Group" : directTitle,
    lastMessageAt: c.lastMessageAt,
    lastMessagePreview: c.lastMessagePreview,
    unreadCount: c.unreadCount ?? 0,
    isEnded: c.isEnded,
    counterpartId: c.counterpartId,
    type: c.type,
    members: c.members,
    memberCount: c.memberCount,
    isArchived: !!c.isArchived,
    isPinned: !!c.isPinned,
    raw: c,
  };
}

export default chatAxios;
